import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getWorkflow, patchWorkflow, saveDraft, publishWorkflow, createWorkflow, getWorkflowVersion,
} from 'features/agents/api/agentsApi';
import { NODE_CATALOG, validateNode } from 'features/agents/WorkflowEditor/nodeCatalog';
import * as Def from 'features/agents/WorkflowEditor/definition';
import { useAppDialog } from 'shared/context/AppDialogContext';

export const DEFAULT_WORKFLOW_NAME = 'Untitled agent';

function useDebouncedCallback(cb, delay) {
  const cbRef = useRef(cb);
  const tRef = useRef(null);
  const argsRef = useRef(null);
  useEffect(() => { cbRef.current = cb; }, [cb]);
  const debounced = useCallback((...args) => {
    argsRef.current = args;
    if (tRef.current) clearTimeout(tRef.current);
    tRef.current = setTimeout(() => {
      tRef.current = null;
      const a = argsRef.current; argsRef.current = null;
      cbRef.current(...a);
    }, delay);
  }, [delay]);
  debounced.flush = useCallback(async () => {
    if (tRef.current) {
      clearTimeout(tRef.current);
      tRef.current = null;
      const a = argsRef.current; argsRef.current = null;
      if (a) await cbRef.current(...a);
    }
  }, []);
  return debounced;
}

/**
 * The agent being edited: loading it, the definition and every edit to it,
 * autosave of the draft, naming, publishing and previewing an old version.
 *
 * The definition (nodes, edges, nothing else) is the state. What the canvas
 * draws is derived from it, with positions from `layoutGraph`; nothing about
 * the picture is ever saved.
 *
 * /agents/new is a draft: nothing exists on the server until Save, so leaving
 * without saving leaves nothing behind.
 */
export default function useAgentEditor({ team, workflowId }) {
  const isDraft = workflowId === 'new';
  const navigate = useNavigate();
  const { prompt: appPrompt, details: appDetails } = useAppDialog();

  const [workflow, setWorkflow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingState, setSavingState] = useState('saved'); // saved | saving | dirty
  const [publishing, setPublishing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [definition, setDefinition] = useState(Def.emptyDefinition());
  const [serverValidation, setServerValidation] = useState(null);
  const [openNodeId, setOpenNodeId] = useState(null);
  const [previewVersion, setPreviewVersion] = useState(null);
  const draftBeforePreviewRef = useRef(null);

  // "Saved" shows for a moment after an autosave finishes.
  const [savedFlash, setSavedFlash] = useState(false);
  const prevSavingRef = useRef(null);
  useEffect(() => {
    if (prevSavingRef.current === 'saving' && savingState === 'saved') {
      setSavedFlash(true);
      const t = setTimeout(() => setSavedFlash(false), 1500);
      prevSavingRef.current = savingState;
      return () => clearTimeout(t);
    }
    prevSavingRef.current = savingState;
    return undefined;
  }, [savingState]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (isDraft) {
        setWorkflow({ id: null, name: '', description: null, status: 'draft_only', published_version_id: null, draft_definition: Def.emptyDefinition() });
        setNameDraft('');
        setDefinition(Def.emptyDefinition());
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const wf = await getWorkflow(workflowId);
        if (cancelled) return;
        setWorkflow(wf);
        setNameDraft(wf.name);
        setDefinition(Def.normalize(wf.draft_definition));
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.error || e.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [workflowId, isDraft]);

  const persistDraft = useDebouncedCallback(async (def) => {
    if (isDraft) return; // a draft is saved only by its Save button
    try {
      setSavingState('saving');
      const saved = await saveDraft(workflowId, def);
      setServerValidation(saved?.validation || null);
      setSavingState('saved');
    } catch (e) {
      setSavingState('dirty');
      setError(e?.response?.data?.error || e.message);
    }
  }, 600);

  const triggerNode = useMemo(() => Def.triggerOf(definition), [definition]);

  const hasUnpublishedChanges = useMemo(() => {
    if (!workflow?.published_version_id) return definition.nodes.length > 0;
    const pubDef = workflow.published_version?.definition;
    if (!pubDef) return false;
    return Def.stableKey(definition) !== Def.stableKey(pubDef);
  }, [workflow, definition]);

  // Autosave on every edit (not while previewing an old version, not on load).
  const firstLoad = useRef(true);
  useEffect(() => {
    if (loading || previewVersion) return;
    if (firstLoad.current) { firstLoad.current = false; return; }
    setSavingState('dirty');
    persistDraft(definition);
  }, [definition, persistDraft, loading, previewVersion]);

  /* ── version preview ───────────────────────────────────────────────── */

  const exitVersionPreview = useCallback(() => {
    if (draftBeforePreviewRef.current) {
      setDefinition(draftBeforePreviewRef.current);
      draftBeforePreviewRef.current = null;
    }
    setPreviewVersion(null);
  }, []);

  /** Returns true when a version is now being previewed (so the caller can close what it must). */
  const previewVersionOf = useCallback(async (versionMeta) => {
    if (previewVersion?.id === versionMeta.id) { exitVersionPreview(); return false; }
    try {
      const full = await getWorkflowVersion(workflowId, versionMeta.id);
      if (!full?.definition) return false;
      if (!draftBeforePreviewRef.current) draftBeforePreviewRef.current = definition;
      setDefinition(Def.normalize(full.definition));
      setPreviewVersion({ id: versionMeta.id, version_number: versionMeta.version_number });
      setOpenNodeId(null);
      return true;
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to load version');
      return false;
    }
  }, [workflowId, previewVersion, definition, exitVersionPreview]);

  /** After "Make live" or "Restore": the server changed the draft, so reload it. */
  const reloadAfterVersionChange = useCallback(async () => {
    draftBeforePreviewRef.current = null;
    setPreviewVersion(null);
    const fresh = await getWorkflow(workflowId);
    setWorkflow(fresh);
    setDefinition(Def.normalize(fresh.draft_definition));
    setSavingState('saved');
  }, [workflowId]);

  const refreshWorkflow = useCallback(async () => {
    const fresh = await getWorkflow(workflowId).catch(() => null);
    if (fresh) setWorkflow(fresh);
  }, [workflowId]);

  /* ── editing ───────────────────────────────────────────────────────── */

  const edit = useCallback((fn) => {
    if (previewVersion) return;
    setDefinition((d) => fn(d));
  }, [previewVersion]);

  /** Add a step of `kind`: after `anchor.fromId` (taking over what followed on that branch), else at the end. */
  const addNode = useCallback((kind, anchor) => {
    const spec = NODE_CATALOG[kind];
    if (!spec) return;
    if (spec.category === 'trigger' && triggerNode) { setError('This agent already has a trigger. Remove it to choose another.'); return; }
    const node = Def.makeNode(kind);
    edit((d) => {
      // Hang the new step off the anchor, else off the last step in the flow.
      let after = anchor?.fromId || null;
      let port = anchor?.port ?? null;
      if (!after && spec.category !== 'trigger') {
        const sinks = d.nodes.filter((n) => !d.edges.some((e) => e.from === n.id));
        after = (sinks[sinks.length - 1] || d.nodes[d.nodes.length - 1])?.id || null;
        port = null;
      }
      return Def.addNode(d, node, { after, port, insert: !!anchor?.fromId });
    });
    setOpenNodeId(node.id);
  }, [edit, triggerNode]);

  /** Add a step between the two ends of one edge, leaving the other edges alone. */
  const addNodeOnEdge = useCallback((kind, edge) => {
    const spec = NODE_CATALOG[kind];
    if (!spec || !edge) return;
    if (spec.category === 'trigger') { setError('A trigger starts an agent; it cannot go between steps.'); return; }
    const node = Def.makeNode(kind);
    edit((d) => {
      let next = Def.addNode(d, node);
      next = Def.disconnect(next, edge.from, edge.to, edge.port ?? null);
      next = Def.connect(next, edge.from, node.id, edge.port ?? null);
      return Def.connect(next, node.id, edge.to, Def.outPortOf(node));
    });
    setOpenNodeId(node.id);
  }, [edit]);

  const updateNode = useCallback((id, patch) => edit((d) => Def.updateNode(d, id, patch)), [edit]);
  const deleteNode = useCallback((id) => {
    edit((d) => Def.removeNode(d, id));
    setOpenNodeId((cur) => (cur === id ? null : cur));
  }, [edit]);

  const onConnect = useCallback((conn) => {
    edit((d) => Def.connect(d, conn.source, conn.target, conn.sourceHandle || null));
  }, [edit]);
  const removeEdge = useCallback((e) => {
    edit((d) => Def.disconnect(d, e.from, e.to, e.port ?? null));
  }, [edit]);
  const onEdgesDelete = useCallback((removed) => {
    edit((d) => removed.reduce((acc, e) => Def.disconnect(acc, e.source, e.target, e.sourceHandle || null), d));
  }, [edit]);

  /* ── naming, saving, publishing ────────────────────────────────────── */

  // The draft's Save: name it (asked in a popup if it has none), create it
  // with the steps on the canvas, then move to its real address.
  const saveNewAgent = useCallback(async () => {
    let name = nameDraft.trim();
    let description;
    if (!name || /^untitled\b/i.test(name)) {
      const details = await appDetails('Name this agent', {
        namePlaceholder: 'e.g. Refund triage', descriptionPlaceholder: 'What this agent does and when it runs', confirmLabel: 'Save',
      });
      if (!details) return;
      name = details.name; description = details.description || undefined;
      setNameDraft(name);
    }
    setCreating(true); setError(null);
    try {
      const wf = await createWorkflow({ name, description });
      if (definition.nodes.length) await saveDraft(wf.id, definition);
      navigate(`/${encodeURIComponent(team)}/agents/${wf.id}`, { replace: true });
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data?.error || e.message);
    } finally { setCreating(false); }
  }, [nameDraft, appDetails, definition, navigate, team]);

  const renameWorkflow = useCallback(async () => {
    if (isDraft) return; // a draft's name is saved with it
    if (!nameDraft.trim() || nameDraft === workflow?.name) return;
    try { setWorkflow(await patchWorkflow(workflowId, { name: nameDraft.trim() })); }
    catch (e) { setError(e?.response?.data?.error || e.message); }
  }, [isDraft, nameDraft, workflow, workflowId]);

  const publish = useCallback(async () => {
    const isUnnamed = !workflow?.published_version_id && (nameDraft || workflow?.name || '').trim() === DEFAULT_WORKFLOW_NAME;
    if (isUnnamed) {
      const entered = await appPrompt('Give this agent a name before publishing.', { title: 'Name your agent', placeholder: 'e.g. Refund triage', defaultValue: '', confirmLabel: 'Continue' });
      if (entered == null) return;
      const trimmed = entered.trim();
      if (trimmed && trimmed !== DEFAULT_WORKFLOW_NAME) {
        try {
          const updated = await patchWorkflow(workflowId, { name: trimmed });
          setWorkflow((w) => ({ ...(w || {}), name: updated.name }));
          setNameDraft(updated.name);
        } catch (e) { setError(e?.response?.data?.error || e.message); return; }
      }
    }
    setPublishing(true);
    try {
      await persistDraft.flush();
      const version = await publishWorkflow(workflowId);
      const fresh = await getWorkflow(workflowId);
      setWorkflow({
        ...fresh,
        published_version_id: fresh.published_version_id || version?.id,
        published_version: fresh.published_version || (version ? { id: version.id, version_number: version.version_number, definition: version.definition, published_at: version.published_at } : null),
      });
    } catch (e) {
      const v = e?.response?.data?.validation;
      if (v?.errors?.length) {
        setServerValidation(v);
        const first = v.errors[0];
        setError(first.message);
        if (first.node_id) setOpenNodeId(first.node_id);
      } else {
        setError(e?.response?.data?.error || e.message || 'Publish failed');
      }
    } finally { setPublishing(false); }
  }, [workflow, nameDraft, appPrompt, workflowId, persistDraft]);

  /* ── what the canvas and the top bar read ──────────────────────────── */

  const problems = useMemo(() => {
    const local = Def.problemsOf(definition);
    const server = (serverValidation?.errors || []).map((e) => ({ ...e, server: true }));
    return [...server, ...local];
  }, [definition, serverValidation]);
  // validateNode is what the settings panel uses; problemsOf covers the graph.
  const problemByNode = useMemo(() => {
    const m = {};
    for (const p of problems) if (p.node_id && !p.warning && !m[p.node_id]) m[p.node_id] = p.message;
    for (const n of definition.nodes) {
      if (m[n.id]) continue;
      const v = validateNode(n);
      if (!v.ok) m[n.id] = v.message;
    }
    return m;
  }, [problems, definition.nodes]);
  const blocking = problems.find((p) => !p.warning) || null;
  const openNode = useMemo(() => Def.nodeById(definition, openNodeId), [definition, openNodeId]);

  return {
    isDraft, workflow, loading, error, setError, definition, triggerNode, openNode, openNodeId, setOpenNodeId,
    nameDraft, setNameDraft, savingState, savedFlash, publishing, creating,
    hasUnpublishedChanges, previewVersion, problems, problemByNode, blocking,
    flushDraft: persistDraft.flush,
    exitVersionPreview, previewVersionOf, reloadAfterVersionChange, refreshWorkflow,
    addNode, addNodeOnEdge, updateNode, deleteNode, onConnect, onEdgesDelete, removeEdge,
    saveNewAgent, renameWorkflow, publish,
  };
}
